import { TeamsFeatureFlags, TeamsSubscriptionPlans } from "@teams/types";
import { SurveyDTO, UserDTO, UserSubscriptionDTO } from "@teams/types/api";
import moment from "moment";

export const isTrialAvailable = (
  user: UserDTO,
  plan: TeamsSubscriptionPlans | TeamsSubscriptionPlans[]
) => {
  return !user.subscriptions.some(sub => includesPlan(plan, sub));
};

export const isSubscriptionExpired = (
  user: UserDTO,
  plan: TeamsSubscriptionPlans | TeamsSubscriptionPlans[]
) => {
  return user.subscriptions.some(sub => {
    return includesPlan(plan, sub) && (sub.state === "suspended" || sub.state === "ended");
  });
};

const includesPlan = (plan: string | TeamsSubscriptionPlans[], sub: UserSubscriptionDTO) => {
  return Array.isArray(plan) ? plan.includes(sub.plan) : sub.plan === plan;
};

export const getTrialExpiresAt = (
  subscription: UserSubscriptionDTO,
  featureFlags: TeamsFeatureFlags
): Date => {
  let trialExpiresAt = subscription.trialExpiresAt;
  if (!trialExpiresAt) {
    let trialLength: number = featureFlags.trialDays;
    if (subscription.plan === "agile-standup") {
      trialLength = featureFlags.agileStandupTrialLength;
    }
    trialExpiresAt = moment(subscription.createdAt).add(trialLength, "days").toDate();
  }
  return trialExpiresAt;
};

export const isStaleTrialSub = (
  subscription: UserSubscriptionDTO | undefined,
  featureFlags: TeamsFeatureFlags
): boolean => {
  if (
    !subscription ||
    subscription.type !== "trial" ||
    subscription.state !== "ended" ||
    subscription.plan === "agile-standup"
  ) {
    return false;
  }
  let trialExpiresAt = getTrialExpiresAt(subscription, featureFlags);
  return moment(trialExpiresAt).add(featureFlags.trialDays, "days").isBefore();
};

export const canReadSurveyResults = (survey: SurveyDTO): boolean => {
  if (survey.role === "own" || survey.resultsVisibility === "real-time") {
    return true;
  }

  if (survey.resultsVisibility === "private") {
    return false;
  }

  if (survey.resultsVisibility === "after-close" && survey.closeAt && survey.closeAt > new Date()) {
    return false;
  }

  return true;
};

export const isSurveyResultsRestrictedForUser = (user: UserDTO, survey?: SurveyDTO): boolean => {
  if (!survey || !user.featureFlags.responseUsageTracking || !isFreeUserOrExpiredTrial(user)) {
    return false;
  }
  return moment(survey.deliverAt).add(user.featureFlags.responseRetentionLimit, "days").isBefore();
};

export const getFreeUserResultsLimitText = (user: UserDTO) => {
  return `Upgrade your account to view results that are older than ${user.featureFlags.responseRetentionLimit} days.`;
};

export const getFreeUserResultsUnavailableText = (user: UserDTO) => {
  return `Results over ${user.featureFlags.responseRetentionLimit} days old are not available on free subscription`;
};

export function getTrialDaysLeftText(
  subscription: UserSubscriptionDTO,
  featureFlags: TeamsFeatureFlags,
  compact?: boolean
): React.ReactNode {
  const trialExpiresAt = getTrialExpiresAt(subscription, featureFlags);
  if (moment().isAfter(trialExpiresAt)) {
    return "Expired";
  }
  return `${moment(trialExpiresAt).fromNow(true)}${!compact ? " left" : ""}`;
}

export const shouldShowResponseTrackingMeter = (user: UserDTO) => {
  if (
    !user.featureFlags.responseUsageTracking ||
    user.featureFlags.responseUsageLimit === Number.MAX_SAFE_INTEGER
  ) {
    return false;
  }

  const activeSub = user.subscriptions.some(sub => sub.state === "active" && sub.type === "full");

  if (activeSub || !userIsOverWarningLimit(user)) {
    return false;
  }

  return true;
};

export const userIsOverWarningLimit = (user: UserDTO) => {
  const usagePercentage = (user.usage / user.featureFlags.responseUsageLimit) * 100;

  return usagePercentage >= user.featureFlags.responseUsageWarningPercentage;
};

export const userIsOverUsageLimit = (user: UserDTO) => {
  if (!user.featureFlags.responseUsageTracking) {
    return false;
  }
  return user.usage >= user.featureFlags.responseUsageLimit;
};

export const userCanCreate = (user: UserDTO) => {
  if (!user.featureFlags.responseUsageTracking) {
    return true;
  }

  const activeSub = user.subscriptions.find(s => s.state === "active");
  if (activeSub) {
    return true;
  }

  return !userIsOverUsageLimit(user);
};

export const isFreeUserOrExpiredTrial = (user: UserDTO) => {
  return (
    !user.subscriptions.length ||
    user.subscriptions.some(s => s.type === "trial" && s.state === "ended")
  );
};
